import { useCallback, useMemo } from 'react';

import {
  CheckoutEntryController,
  CheckoutEntryDetails,
  CheckoutEntrySelection,
} from '../types';
import { buildDrawOptionalItems } from '../utils/drawIntent';
import { sameOrderItems } from '../utils/orderItems';
import { buildPricingOrderTotals } from '../utils/orderTotals';
import {
  ConfirmedDrawEntryIntent,
  useDrawEntryConfirmation,
} from './useDrawEntryConfirmation';
import { useDrawEntryIntent } from './useDrawEntryIntent';

export type UseDrawEntryArgs = {
  drawId: string | null;
  onConfirmed: (intent: ConfirmedDrawEntryIntent) => Promise<void>;
  productId: string | null;
};

export const useDrawEntry = ({
  drawId,
  onConfirmed,
  productId,
}: UseDrawEntryArgs): CheckoutEntryController => {
  const {
    clientSecret,
    error,
    intent,
    loading,
    pricing,
    reset,
    submit: submitIntent,
    submittedInput,
  } = useDrawEntryIntent();
  const { confirmEntry, confirming } = useDrawEntryConfirmation({
    onConfirmed,
  });

  const toOptionalItems = useCallback(
    (selection: CheckoutEntrySelection) => {
      return buildDrawOptionalItems({
        framingSelections: selection.framingSelections,
        productId,
      });
    },
    [productId],
  );

  const covers = useCallback(
    (selection: CheckoutEntrySelection) => {
      if (!submittedInput) return true;

      return sameOrderItems(
        submittedInput.optionalItems,
        toOptionalItems(selection),
      );
    },
    [submittedInput, toOptionalItems],
  );

  const submit = useCallback(
    async (
      selection: CheckoutEntrySelection,
      details: CheckoutEntryDetails,
    ) => {
      if (!drawId || !selection.selectedDraws.includes(drawId)) return false;

      const created = await submitIntent({
        drawId,
        currencyCode: details.currencyCode,
        shippingAddressId: details.shippingAddressId,
        paymentMethodId: details.paymentMethodId,
        preOrder: details.preOrder,
        optionalItems: toOptionalItems(selection),
        customAttributes: details.customAttributes,
      });

      return Boolean(created);
    },
    [drawId, submitIntent, toOptionalItems],
  );

  const quote = useMemo(() => {
    return pricing ? buildPricingOrderTotals(pricing) : null;
  }, [pricing]);

  return useMemo(
    () => ({
      clientSecret,
      confirmEntry,
      confirming,
      covers,
      error,
      intentId: intent?.id ?? null,
      loading,
      quote,
      quoteCurrencyCode: pricing?.currencyCode ?? null,
      reset,
      submit,
    }),
    [
      clientSecret,
      confirmEntry,
      confirming,
      covers,
      error,
      intent,
      loading,
      pricing,
      quote,
      reset,
      submit,
    ],
  );
};
